import { useCallback, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Button, Card, Divider, Drawer, Empty, Form, Grid, Space, Tag, Typography, theme, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import type { ClinicalRecord } from '../api/clinicalHistory.api';
import type { Appointment, AppointmentHistoryEntry, AppointmentServiceLine } from '@erp/appointments/api/appointments.api';
import {
  useAppointmentHistory,
  useCreateAppointmentHistoryEntry,
  useDeleteAppointmentHistoryEntry,
  useUpdateAppointmentHistoryEntry,
  appointmentsExpandedQueryKey,
  appointmentsListKey,
} from '@erp/appointments/api/appointments.api';
import { useAppointmentPermissions } from '@erp/appointments/hooks/useAppointmentPermissions';
import type { StaffDirectoryEntry } from '@erp/services/api/serviceStaff';
import { AppointmentHistorySection } from '@erp/appointments/components/AppointmentHistorySection';
import { formatServiceLineLabel } from '../utils/serviceLineLabel';
import RichTextDisplay from '@tools/components/RichTextDisplay';
import HistoryEntriesTimeline from '@erp/appointments/components/history/HistoryEntriesTimeline';
import HistoryEntryFormModal from '@erp/appointments/components/history/HistoryEntryFormModal';
import {
  buildEntryInitialValues,
  buildHistoryDraft,
  buildHistoryStaffLabel,
  buildStaffOptions,
  mergeStaffOptions,
  type HistoryFormValues,
  type StaffOption,
} from '@erp/appointments/components/history/historyHelpers';
import { useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@store/store';

const { Text, Title } = Typography;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  scheduled: 'Agendado',
  attended: 'Atendido',
  no_show: 'Ausente',
  canceled: 'Cancelado',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  attended: 'green',
  no_show: 'volcano',
  canceled: 'red',
};

export interface ClinicalHistoryDrawerProps {
  open: boolean;
  record: ClinicalRecord | null;
  appointments: Appointment[];
  staffDirectory: StaffDirectoryEntry[];
  onClose: () => void;
  onOpenAppointment?: (appointmentId: number) => void;
}

const isValidationError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'errorFields' in error;

export function ClinicalHistoryDrawer({
  open,
  record,
  appointments,
  staffDirectory,
  onClose,
  onOpenAppointment,
}: ClinicalHistoryDrawerProps): JSX.Element {
  const screens = Grid.useBreakpoint();
  const drawerWidth = screens.md ? 650 : '100%';
  const queryClient = useQueryClient();
  const { token } = theme.useToken();

  const [form] = Form.useForm<HistoryFormValues>();
  const [isFormModalOpen, setFormModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AppointmentHistoryEntry | null>(null);
  const [extraStaffOption, setExtraStaffOption] = useState<StaffOption | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [editingServiceLines, setEditingServiceLines] = useState<AppointmentServiceLine[] | undefined>(undefined);

  const { canManageHistory, canManageHistoryOwn, canDeleteHistory } = useAppointmentPermissions();
  const currentStaffId = useAppSelector((state) => state.user.profile?.staff_user_id ?? null);

  const appointmentId = record?.id ?? 0;
  const historyQuery = useAppointmentHistory(appointmentId, {
    enabled: open && Boolean(record),
    staleTime: 30_000,
  });

  const createMutation = useCreateAppointmentHistoryEntry();
  const updateMutation = useUpdateAppointmentHistoryEntry();
  const deleteMutation = useDeleteAppointmentHistoryEntry();

  const isOwnAppointment =
    record?.staff_user_id != null && currentStaffId != null && record.staff_user_id === currentStaffId;
  const canCreateEntry = canManageHistory || (canManageHistoryOwn && isOwnAppointment);
  const canEditEntries = canDeleteHistory;
  const canDeleteEntries = canDeleteHistory;

  const baseStaffOptions = useMemo(() => buildStaffOptions(staffDirectory), [staffDirectory]);
  const staffOptions = useMemo(
    () => mergeStaffOptions(baseStaffOptions, extraStaffOption),
    [baseStaffOptions, extraStaffOption],
  );

  const ensureStaffOption = useCallback(
    (staffId?: number | null, entry?: AppointmentHistoryEntry) => {
      if (staffId == null) {
        setExtraStaffOption(null);
        return;
      }
      const exists = staffDirectory.some((staff) => staff.id === staffId);
      setExtraStaffOption(
        exists
          ? null
          : {
              value: staffId,
              label: buildHistoryStaffLabel(staffId, staffDirectory, entry),
            },
      );
    },
    [staffDirectory],
  );

  const closeFormModal = useCallback(() => {
    setFormModalOpen(false);
    setEditingEntry(null);
    setEditingServiceLines(undefined);
    setExtraStaffOption(null);
    form.resetFields();
  }, [form]);

  const handleStartCreate = useCallback(() => {
    if (!canCreateEntry) {
      message.error('No tenés permisos para registrar historial');
      return;
    }
    const defaultStaff = record?.staff_user_id ?? staffOptions[0]?.value ?? undefined;
    setEditingEntry(null);
    setEditingServiceLines(record?.service_lines);
    ensureStaffOption(record?.staff_user_id);
    form.setFieldsValue({
      staff_user_id: defaultStaff,
      notes: undefined,
    });
    setFormModalOpen(true);
  }, [canCreateEntry, ensureStaffOption, form, record?.staff_user_id, record?.service_lines, staffOptions]);

  const handleEditEntry = useCallback(
    (entry: AppointmentHistoryEntry) => {
      if (!canEditEntries) {
        message.error('No tenés permisos para editar registros');
        return;
      }
      setEditingEntry(entry);
      setEditingServiceLines(record?.service_lines);
      ensureStaffOption(entry.staff_user_id, entry);
      form.setFieldsValue(buildEntryInitialValues(entry));
      setFormModalOpen(true);
    },
    [canEditEntries, ensureStaffOption, form, record?.service_lines],
  );

  const handleDeleteEntry = useCallback(
    async (entry: AppointmentHistoryEntry) => {
      if (!canDeleteEntries) {
        message.error('No tenés permisos para eliminar registros');
        return;
      }
      try {
        setPendingDeleteId(entry.id);
        await deleteMutation.mutateAsync({
          appointmentId: entry.appointment_id,
          entryId: entry.id,
        });
        message.success('Registro eliminado');
        void historyQuery.refetch();
        void queryClient.invalidateQueries({ queryKey: appointmentsExpandedQueryKey });
        void queryClient.invalidateQueries({ queryKey: appointmentsListKey });
      } catch {
        message.error('No pudimos eliminar el registro');
      } finally {
        setPendingDeleteId(null);
      }
    },
    [canDeleteEntries, deleteMutation, historyQuery, queryClient],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (!values.staff_user_id) {
        message.error('No hay profesional seleccionado');
        return;
      }
      const payload = buildHistoryDraft(values);
      if (editingEntry) {
        await updateMutation.mutateAsync({
          appointmentId: editingEntry.appointment_id,
          entryId: editingEntry.id,
          payload,
        });
        message.success('Registro actualizado');
      } else {
        await createMutation.mutateAsync({
          appointmentId,
          payload,
        });
        message.success('Nota registrada en el historial');
      }
      closeFormModal();
      void historyQuery.refetch();
      void queryClient.invalidateQueries({ queryKey: appointmentsExpandedQueryKey });
      void queryClient.invalidateQueries({ queryKey: appointmentsListKey });
    } catch (error) {
      if (!isValidationError(error)) {
        message.error('No pudimos guardar la nota');
      }
    }
  }, [appointmentId, closeFormModal, createMutation, editingEntry, form, historyQuery, queryClient, updateMutation]);

  const summary = useMemo(() => {
    if (!record) return null;
    const servicesLabel =
      record.service_lines.length > 0
        ? record.service_lines.map(formatServiceLineLabel).join(', ')
        : 'Sin servicio detallado';
    const staffLabel = record.staff_name || `Staff #${record.staff_user_id}`;
    const spaceLabel = record.space_name || `Espacio #${record.space_id}`;
    const patientLabel = record.customer_name || record.customer_username || 'Sin asignar';
    const scheduleLabel = `${dayjs(record.start_at).format('DD/MM/YYYY HH:mm')} - ${dayjs(record.end_at).format('HH:mm')}`;

    return {
      servicesLabel,
      staffLabel,
      spaceLabel,
      patientLabel,
      scheduleLabel,
    };
  }, [record]);

  const historyEntries = historyQuery.data?.history ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={drawerWidth}
      title="Historia Clínica de Turno"
      destroyOnHidden
      placement="right"
    >
      {record && summary ? (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {/* Encabezado y Resumen */}
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            <Title level={4} style={{ marginBottom: 0 }}>
              <span style={{fontWeight: 'lighter'}}>Turno</span> {summary.patientLabel}
            </Title>
            <Text type="secondary">{summary.scheduleLabel}</Text>
            <Space size={8} wrap style={{ marginTop: 4 }}>
              <Tag color="blue">{summary.servicesLabel}</Tag>
              <Tag>{summary.staffLabel}</Tag>
              <Tag color="purple">{summary.spaceLabel}</Tag>
              <Tag color={STATUS_COLORS[record.status]}>
                Estado: {STATUS_LABELS[record.status] ?? record.status}
              </Tag>
            </Space>
          </Space>

          {record.notes ? (
            <Card
              size="small"
              style={{
                backgroundColor: token.colorFillAlter,
                borderColor: token.colorBorderSecondary,
              }}
            >
              <Space orientation="vertical" size={0}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Notas del turno
                </Text>
                <Text>{record.notes}</Text>
              </Space>
            </Card>
          ) : null}

          {/* Sección de Registro / Nota Clínica Actual */}
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Title level={5} style={{ marginBottom: 0 }}>
                Registro / Historia Clínica
              </Title>
              {canCreateEntry ? (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleStartCreate}
                >
                  Agregar Registro
                </Button>
              ) : null}
            </div>

            {record.latest_history_note ? (
              <Card
                size="small"
                style={{
                  borderLeft: `4px solid ${token.colorPrimary}`,
                  backgroundColor: token.colorPrimaryBg,
                  borderColor: token.colorBorderSecondary,
                }}
              >
                <Space
                  orientation="vertical"
                  size={4}
                  style={{ width: '100%' }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Última evolución (
                    {record.latest_history_recorded_by ?? 'Registrado'})
                  </Text>
                  <RichTextDisplay value={record.latest_history_note} />
                </Space>
              </Card>
            ) : (
              <Card
                size="small"
                style={{
                  backgroundColor: token.colorFillTertiary,
                  border: `1px dashed ${token.colorBorderSecondary}`,
                }}
              >
                <Text type="secondary" style={{ fontStyle: 'italic' }}>
                  Este turno aún no posee historia clínica / evolución
                  registrada.
                </Text>
              </Card>
            )}
          </Space>

          {/* Histórico completo de evoluciones de este turno */}
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <Title level={5} style={{ marginBottom: 4 }}>
              Histórico de Registros del Turno ({historyEntries.length})
            </Title>
            <HistoryEntriesTimeline
              entries={historyEntries}
              loading={historyQuery.isLoading}
              staffDirectory={staffDirectory}
              canEdit={canEditEntries}
              canDelete={canDeleteEntries}
              deleteLoading={deleteMutation.isPending}
              pendingDeleteId={pendingDeleteId}
              onEdit={handleEditEntry}
              onDelete={handleDeleteEntry}
            />
          </Space>

          <Divider />

          {/* Turnos anteriores del cliente */}
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <Title level={5} style={{ marginBottom: 4 }}>
              Historial de Otros Turnos del Paciente
            </Title>
            <AppointmentHistorySection
              appointmentId={record.id}
              appointments={appointments}
              staffDirectory={staffDirectory}
              defaultStaffId={record.staff_user_id}
              customerId={record.customer_id}
              onOpenAppointment={onOpenAppointment}
            />
          </Space>
        </Space>
      ) : (
        <Empty description="Seleccioná una historia clínica para ver su detalle" />
      )}

      <HistoryEntryFormModal
        open={isFormModalOpen}
        editingEntry={editingEntry}
        form={form}
        staffOptions={staffOptions}
        confirmLoading={
          editingEntry ? updateMutation.isPending : createMutation.isPending
        }
        onCancel={closeFormModal}
        onSubmit={handleSubmit}
        serviceLines={editingServiceLines}
      />
    </Drawer>
  );
}
